import React from "react";
import {
  AlertIcon,
  BookIcon,
  BriefcaseIcon,
  CornerDownRightIcon,
  GlobeIcon,
  HeartIcon,
  LayoutIcon,
  LightningIcon,
  MinusCircleIcon,
  MoneyIcon,
  PersonIcon,
  PersonWithCheckIcon,
  PinIcon,
  RefreshIcon,
  SearchIcon,
  SettingsIcon,
  SparklesIcon,
  TargetIcon,
  TrendIcon,
} from "ui/icons";
import hero from "../../../../public/assets/cdp-industries/heros/education.png";
import { dataBreachStat, personalizedExperiencesStat, revenueReferenceCopy, type CdpIndustryConfig } from "./shared";

export const educationCdpConfig: CdpIndustryConfig = {
  icon: BookIcon,
  name: "education",
  pluralNoun: "education companies",
  color: "red",
  seoCopy:
    "Leverage all your education data, including students, faculty, courses, enrollments, applications, and grades.",
  heroImage: {
    src: hero,
    alt: "A photograph of a student holding a tablet with a list of example data tables overlayed. The data tables included are for students, faculty, grades, courses, enrollment, and applications.",
  },
  adoptingCdpsDescription: (
    <>
      Education is no longer one-size-fits-all. Students and families demand programs that are tailored specifically to
      their goals. {revenueReferenceCopy} If you’re not leveraging data to deliver more personalized experiences,
      students and families will simply choose another institution.
    </>
  ),
  featuredOutcomes: [
    {
      icon: TrendIcon,
      color: "blue",
      title: "Boost engagement",
    },
    {
      icon: PersonWithCheckIcon,
      color: "green",
      title: "Increase conversions",
    },
    {
      icon: HeartIcon,
      color: "red",
      title: "Build loyalty",
    },
  ],
  dataStats: [
    {
      title: "$1.7T",
      description: (
        <>
          Estimated student
          <br />
          loan debt in the US
        </>
      ),
      href: "https://www.federalreserve.gov/releases/g19/HIST/cc_hist_memo_levels.html",
    },
    personalizedExperiencesStat,
    dataBreachStat,
  ],
  personalizationCards: [
    {
      title:
        "Duolingo leverages AI decisioning to send millions of personalized push notifications to increase product usage",
      href: "https://blog.duolingo.com/hi-its-duo-the-ai-behind-the-meme/",
    },
    {
      title:
        "Khan Academy launched a new AI feature to tailor tutoring sessions to the learner interests and preferences",
      href: "https://blog.khanacademy.org/new-khanmigo-interests/",
    },
    {
      title:
        "Coursera implemented AI-powered virtual coach to help answer questions and provide personalized feedback to learners increasing quiz pass rates by 9.5%",
      href: "https://blog.coursera.org/announcing-ai-powered-capabilities-enabling-educators-to-use-coursera-coach-to-deliver-interactive-personalized-instruction/",
    },
  ],
  useCases: {
    performanceMarketing: [
      // First row
      {
        icon: PersonWithCheckIcon,
        title: "Acquisition campaigns",
        description:
          "Drive new student enrollments by using demographic and interest data to target prospective students and lookalike audiences with program-specific promotions, increasing registrations.",
      },
      {
        icon: MinusCircleIcon,
        title: "Suppression campaigns",
        description:
          "Reduce wasted ad spend by suppressing currently enrolled students from acquisition campaigns using CRM and enrollment data, reallocating budget toward prospective or lapsed students.",
      },
      {
        icon: TargetIcon,
        title: "Retargeting campaigns",
        description:
          "Re-engage users who started but didn’t complete applications by serving reminders and limited-time incentives, increasing application completion rates.",
      },
      // Second row
      {
        icon: MoneyIcon,
        title: "Scholarship promotions",
        description:
          "Attract cost-conscious students by advertising partial or full scholarships, increasing inquiries and applications.",
      },
      {
        icon: SettingsIcon,
        title: "Conversion APIs",
        description:
          "Share online and offline conversion data with ad platforms, enabling algorithms to better identify and target users with a higher likelihood of converting to improve campaign performance.",
      },
      {
        icon: GlobeIcon,
        title: "Education media networks",
        description:
          "Monetize segmentation data by offering anonymized student interest and engagement insights to universities and online course providers for targeted advertising.",
      },
    ],
    lifecycleMarketing: [
      // First row
      {
        icon: PersonIcon,
        title: "Upsell & cross-sell campaigns",
        description:
          "Recommend advanced certifications or degree programs to students finishing a course, increasing lifetime enrollments.",
      },
      {
        icon: RefreshIcon,
        title: "Reactivation campaigns",
        description: "Target lapsed students with discounts or enrollment incentives to boost retention.",
      },
      {
        icon: SparklesIcon,
        title: "Graduation celebration campaigns",
        description: "Offer exclusive alumni benefits to maintain lifelong engagement.",
      },
      // Second row
      {
        icon: PinIcon,
        title: "Localized promotions",
        description: "Advertise campus-specific programs or workshops to drive regional attendance.",
      },
      {
        icon: AlertIcon,
        title: "Renewal reminders",
        description: "Encourage students to renew expiring certifications with early renewal discounts.",
      },
      {
        icon: BookIcon,
        title: "Seasonal campaigns",
        description: "Promote back-to-school enrollment, boot camps, or summer courses to increase seasonal sign-ups.",
      },
    ],
    onsitePersonalization: [
      // First row
      {
        icon: LightningIcon,
        title: "Dynamic home page content",
        description:
          "Display personalized recommendations for degree programs, certifications, or workshops based on browsing behavior to increase engagement and application rates.",
      },
      {
        icon: SearchIcon,
        title: "Dynamic search results",
        description:
          "Tailor search results to highlight popular programs, in-demand certifications, or local workshops, improving search-to-application conversions.",
      },
      {
        icon: CornerDownRightIcon,
        title: "Next best action",
        description:
          "Guide prospective students toward key steps like completing applications, attending webinars, or scheduling campus visits to improve conversion rates and engagement.",
      },
      // Second row
      {
        icon: BriefcaseIcon,
        title: "Interactive career tools",
        description:
          "Offer career path simulators, salary projections, or resume-building tools within the app to increase engagement and retention.",
      },
      {
        icon: TrendIcon,
        title: "Course progress tracking",
        description:
          "Display progress toward course or certification completion with visual trackers and personalized encouragement to improve course completion rates.",
      },
      {
        icon: LayoutIcon,
        title: "Personalized learning dashboard",
        description:
          "Highlight courses in progress, upcoming assignments, or recently completed modules to improve student engagement and retention.",
      },
      // Third row
      {
        icon: SparklesIcon,
        title: "Gamified milestones",
        description:
          "Reward students with badges, certificates, or points for hitting learning milestones to drive engagement through gamification.",
      },
      {
        icon: PersonWithCheckIcon,
        title: "Parental tracking features",
        description:
          "Provide real-time progress updates and insights into a child’s assignments and performance to strengthen parental engagement and satisfaction.",
      },
      {
        icon: MoneyIcon,
        title: "Financial aid recommendations",
        description:
          "Display customized financial aid options, grants, and scholarship opportunities based on a student's profile, academic achievements, and financial background to improve accessibility and application rates.",
      },
    ],
  },
  considerations: {
    dataModeling: {
      traditional: "Uses predefined models that may not fully capture enrollment trends or student engagement patterns",
      composable: "Supports tailored models to handle course enrollment, retention rates, and learning progress data",
    },
    audienceManagement: {
      traditional:
        "Supports broad segmentation but struggles with identifying student personas or learning preferences",
      composable:
        "Enables granular segmentation using data on course completion rates, program preferences, and student demographics",
    },
    customerJourneys: {
      traditional: "Provides standard templates for campaigns like application reminders or program promotions",
      composable:
        "Powers fully adaptable journeys for events like student onboarding, course progression, and alumni engagement",
    },
    identityResolution: {
      traditional:
        "Relies on out-of-the-box algorithms that may not unify profiles across learning management systems and enrollment platforms",
      composable:
        "Supports custom algorithms to unify profiles across LMS systems, student portals, and campus databases",
    },
  },
  whyHightouchTitleMaxW: "750px",
  audienceOverlap: {
    title: "Prospective students who didn’t apply",
    firstCategoryLabel: "Incomplete college applications",
    secondCategoryLabel: "Admissions webinar attendees",
  },
};
